/*
  useGraphData.js
*/

// Npm.
import { useState, useCallback, useEffect } from 'react';

// Lib.
import getDefaultScenario from '../../utils/getDefaultScenario';
import botechToGraphData from '../../utils/botechToGraphData';
import graphDataToBotech from '../../utils/graphDataToBotech';
import localStorageJSON from '../../utils/localStorageJSON';

// Hooks.
import usePrevious from '../../hooks/usePrevious';
import useBotech from '../../hooks/useBotech';

// Constants.
const STORAGE_KEY = 'botech_graph_data';
const MAX_HISTORY = 64;

// Memo for API calls.
const _memo = {};

// Helper to get graph data, given a value.
function getDataOrEmpty(data) {
  return data || botechToGraphData(null);
}

/**
 * Hook to get/set the current graph data.
 */
export default function useGraphData() {

  const botech = useBotech();

  const [value, setValue] = useState(() => {

    // If "botech.initialModel" is provided, then start with a blank scenario
    // as we anticipate one will be loaded in later with the effect further down.

    if (botech.initialModel) {
      return botechToGraphData(null);
    }

    // Check for stored scenario.
    // We assume that "botechToGraphData" could throw an error if it's bad data,
    // just for sanity's sake.

    const current = localStorageJSON.getItem(STORAGE_KEY);
    if (current) {
      try {
        return botechToGraphData(current);
      } catch (err) {
        // Invalid data. Ignore.
      }
    }

    // Use default/no scenario as a backup.

    try {
      return botechToGraphData(getDefaultScenario());
    } catch (err) {
      return botechToGraphData(null);
    }

  });

  // Helper to set and store the value.
  // TODO: If this is ever called repeatedly very fast, then consider
  // debouncing the localStorage step.

  const handleSetValue = useCallback((valueOrFunc) => {
    if (typeof valueOrFunc === 'function') {

      setValue(v => {
        try {
          const next = getDataOrEmpty(valueOrFunc(v));
          localStorageJSON.setItem(STORAGE_KEY, graphDataToBotech(next));
          return next;
        } catch (err) {
          console.error(err);
          return v;
        }
      });

    } else {

      try {
        const next = getDataOrEmpty(valueOrFunc);
        localStorageJSON.setItem(STORAGE_KEY, graphDataToBotech(next));
        setValue(next);
      } catch (err) {
        console.log(err);
      }

    }
  }, [setValue]);

  // Graph data history.
  // Listens for changes in graph data to allow for undo/redo.
  // To undo, set "value" to one of the history items.

  // TODO: Make history only log changes to nodes and links.

  const [history, setHistory] = useState([]);
  const lastValue = usePrevious(value);

  useEffect(() => {
    if (!history.includes(value)) {
      let nextHistory = history.slice(0, history.indexOf(lastValue) + 1);
      nextHistory.push(value);
      if (nextHistory.length > MAX_HISTORY) {
        nextHistory = nextHistory.slice(-MAX_HISTORY);
      }
      setHistory(nextHistory);
    }
  }, [lastValue, value, history]);

  // If the botech context starts with a userId and modelId, attempt to load
  // the user'd model using the botech API.
  // TODO: We could add a loading state here, but it doesn't really matter
  // and should only execute once at the beginning, as modelId won't change much.

  useEffect(() => {
    if (botech.userId && botech.modelId) {

      // Check for memoized promise in case the effect
      // runs multiple times on the same user or model.

      const url = `/model/${botech.userId}/${botech.modelId}`;
      const memo = _memo;
      if (!memo[url]) {
        memo[url] = botech.makeApiCall('GET', url).promise;
        memo[url].catch(err => {
          // Caught here to prevent runtime warning.
        }).finally(() => {
          delete memo[url];
        });
      }

      // Await the promise.

      let subbed = true;
      memo[url].then(res => {
        if (subbed) {
          setValue(botechToGraphData(res));
        }
      }).catch(err => {
        if (subbed) {
          // Ignore error.
        }
      });
      return () => {
        subbed = false;
      };

    }
  }, [botech.userId, botech.modelId]);

  // If "botech.initialModel" is given, then use it to try and set the model.
  // If this changes, we assume the user is trying to load in a new model.
  // NOTE: Regular "setValue" used so it won't immediately consume the previous localStorage value.

  useEffect(() => {

    // Check "initialModel".

    let initialModel = botech.initialModel;
    if (typeof initialModel === 'function') {
      initialModel = initialModel();
    }
    if (!initialModel) {
      return;
    }

    // Await the promise.

    let subbed = true;
    Promise.resolve(initialModel).then(res => {
      if (subbed && res) {
        try {
          setValue(botechToGraphData(res));
        } catch (err) {
          // Invalid data. Ignore.
        }
      }
    }).catch(err => {
      if (subbed) {
        // Ignore.
      }
    });
    return () => {
      subbed = false;
    };

  }, [botech.initialModel]);

  return [value, handleSetValue, history];

}
